import { useEffect, useState } from 'react';
import { Globe, Server } from 'lucide-react';
import { configService } from '../../services/usersApi';
import { ServerConfig } from '../../types/player';
import { GLOBAL_CLUSTER_ID } from '../../types/shopChangelog';
import { cn } from '../../utils/cn';

// Module-scoped one-shot cache of the cluster→thumbnail map. Avoids hitting the
// configs endpoint once per row.
let configsPromise: Promise<ServerConfig[]> | null = null;
const thumbnailCache = new Map<string, string>();

function ensureConfigs(): Promise<ServerConfig[]> {
  if (!configsPromise) {
    configsPromise = configService
      .getConfigs()
      .then((configs) => {
        for (const c of configs) {
          if (c.cluster_name && c.thumbnail_url) {
            thumbnailCache.set(c.cluster_name, c.thumbnail_url);
          }
        }
        return configs;
      })
      .catch((err) => {
        // On error reset the promise so a subsequent mount can retry.
        configsPromise = null;
        throw err;
      });
  }
  return configsPromise;
}

interface ShopChangelogClusterPillProps {
  clusterId: string;
  className?: string;
  /** Smaller variant used inside the filter chip list. */
  size?: 'sm' | 'md';
}

/**
 * Inline cluster pill. Special-cases the `_GLOBAL_` bucket with a purple,
 * globe-iconed pill (clearly distinct from per-cluster pushes).
 */
export function ShopChangelogClusterPill({ clusterId, className, size = 'md' }: ShopChangelogClusterPillProps) {
  const isGlobal = clusterId === GLOBAL_CLUSTER_ID;
  const [thumbnail, setThumbnail] = useState<string | null>(() => thumbnailCache.get(clusterId) ?? null);

  useEffect(() => {
    if (isGlobal || thumbnail) return;
    let cancelled = false;
    ensureConfigs()
      .then(() => {
        if (cancelled) return;
        const found = thumbnailCache.get(clusterId);
        if (found) setThumbnail(found);
      })
      .catch(() => {
        // Falls back to the Server icon below.
      });
    return () => {
      cancelled = true;
    };
  }, [clusterId, isGlobal, thumbnail]);

  const padding = size === 'sm' ? 'px-1.5 py-0.5' : 'px-2 py-0.5';
  const text = size === 'sm' ? 'text-[10px]' : 'text-xs';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';

  if (isGlobal) {
    return (
      <span
        title="GLOBAL — change applies to every cluster"
        className={cn(
          'inline-flex items-center gap-1 font-semibold rounded-full border',
          'bg-purple-500/20 text-purple-200 border-purple-400/40',
          padding,
          text,
          className,
        )}
      >
        <Globe className={iconSize} />
        GLOBAL
      </span>
    );
  }

  return (
    <span
      title={clusterId}
      className={cn(
        'inline-flex items-center gap-1.5 font-semibold rounded-full border',
        'bg-glass-200/10 text-glass-100 border-glass-200/30',
        padding,
        text,
        className,
      )}
    >
      {thumbnail ? (
        <img src={thumbnail} alt="" className={cn(iconSize, 'rounded-sm object-cover')} />
      ) : (
        <Server className={cn(iconSize, 'text-glass-300')} />
      )}
      <span>{clusterId}</span>
    </span>
  );
}
